import { useEffect, useMemo, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { KPICard } from "@/components/Dashboard/KPICard";
import { RevenueChart } from "@/components/Dashboard/RevenueChart";
import { MeetingSummary } from "@/components/Dashboard/MeetingSummary";
import { TeamScorecard } from "@/components/Dashboard/TeamScorecard";
import { CoachingSessions } from "@/components/Dashboard/CoachingSessions";
import { TemplateLibrary } from "@/components/Dashboard/TemplateLibrary";
import { KPIData } from "@shared/types";
import { UserProfile } from "@shared/types";
import { getKpiIcon, getStatusFromPercentage, calculatePercentage } from "@/lib/utils";
import { useLocation } from "@/contexts/LocationContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/fetchWithAuth";
// no-op import removed

interface Metric {
  id: string;
  name: string;
  value: string | number;
  goal: string | number;
  status: 'green' | 'yellow' | 'red';
  category: string;
  locationId: string;
  organizationId: string;
  date: string;
  createdAt: string;
}

interface DashboardProps {
  user: UserProfile;
}

// Aggregation helper functions
const aggregateMetrics = (metrics: Metric[], selectedLocationIds: string[]): Metric[] => {
  console.log('🔢 Aggregating metrics for locations:', selectedLocationIds);
  console.log('📊 Input metrics:', metrics.length);

  // Group metrics by name/category
  const metricGroups: Record<string, Metric[]> = {};
  
  metrics.forEach(metric => {
    const key = `${metric.name}-${metric.category}`;
    if (!metricGroups[key]) {
      metricGroups[key] = [];
    }
    metricGroups[key].push(metric);
  });

  // Aggregate each group
  const aggregatedMetrics: Metric[] = [];
  
  Object.entries(metricGroups).forEach(([key, groupMetrics]) => {
    const firstMetric = groupMetrics[0];
    let aggregatedValue: number;
    let aggregatedGoal: number;
    
    // Convert values to numbers for aggregation
    const numericValues = groupMetrics.map(m => typeof m.value === 'string' ? parseFloat(m.value.replace(/[,$]/g, '')) : Number(m.value));
    const numericGoals = groupMetrics.map(m => typeof m.goal === 'string' ? parseFloat(m.goal.replace(/[,$]/g, '')) : Number(m.goal));
    
    // Determine aggregation method based on metric type
    if (firstMetric.name.toLowerCase().includes('rate') || 
        firstMetric.name.toLowerCase().includes('satisfaction') ||
        firstMetric.name.toLowerCase().includes('score')) {
      // Average for rates, satisfaction scores, etc.
      aggregatedValue = numericValues.reduce((sum, val) => sum + val, 0) / numericValues.length;
      aggregatedGoal = numericGoals.reduce((sum, val) => sum + val, 0) / numericGoals.length;
      console.log(`📊 Averaged ${firstMetric.name}: ${aggregatedValue.toFixed(1)}`);
    } else {
      // Sum for revenue, appointments, clients, etc.
      aggregatedValue = numericValues.reduce((sum, val) => sum + val, 0);
      aggregatedGoal = numericGoals.reduce((sum, val) => sum + val, 0);
      console.log(`📊 Summed ${firstMetric.name}: ${aggregatedValue.toLocaleString()}`);
    }
    
    // Format the aggregated values
    const formattedValue = firstMetric.name.toLowerCase().includes('revenue') || firstMetric.name.toLowerCase().includes('dollar')
      ? `$${aggregatedValue.toLocaleString()}`
      : firstMetric.name.toLowerCase().includes('rate') || firstMetric.name.toLowerCase().includes('satisfaction')
      ? `${aggregatedValue.toFixed(1)}%`
      : aggregatedValue.toLocaleString();
      
    const formattedGoal = firstMetric.goal.toString().includes('$')
      ? `$${aggregatedGoal.toLocaleString()}`
      : firstMetric.goal.toString().includes('%')
      ? `${aggregatedGoal.toFixed(1)}%`
      : aggregatedGoal.toLocaleString();

    // Calculate new status based on aggregated values
    const percentage = calculatePercentage(aggregatedValue, aggregatedGoal);
    const newStatus = getStatusFromPercentage(percentage);
    
    aggregatedMetrics.push({
      ...firstMetric,
      id: `aggregated-${firstMetric.category}-${firstMetric.name}`,
      value: formattedValue,
      goal: formattedGoal,
      status: newStatus,
      locationId: 'multi-select' // Special identifier for aggregated data
    });
  });
  
  console.log('✅ Aggregated metrics:', aggregatedMetrics.length);
  return aggregatedMetrics;
};

export default function Dashboard({ user }: DashboardProps) {
  const { selectedLocationId, selectedLocationIds, locations } = useLocation();
  const { currentOrganizationId } = useOrganization();
  const [timeRange, setTimeRange] = useState<string>("last7days");
  const [activeTab, setActiveTab] = useState<'overview' | 'pl' | 'bs' | 'cf'>("overview");
  const [pl, setPL] = useState<any | null>(null);
  const [bs, setBS] = useState<any | null>(null);
  const [cf, setCF] = useState<any | null>(null);
  const [finLoading, setFinLoading] = useState<boolean>(false);

  // Reset to Overview when organization changes
  useEffect(() => {
    setActiveTab('overview');
    setPL(null); setBS(null); setCF(null);
  }, [currentOrganizationId]);

  // Auto-fetch statement when tab is opened
  useEffect(() => {
    const orgParam = currentOrganizationId ? `?organizationId=${currentOrganizationId}` : '';
    const run = async () => {
      try {
        setFinLoading(true);
        if (activeTab === 'pl' && !pl) {
          const data = await apiRequest(`/api/finance/profit-loss${orgParam}`);
          setPL(data);
        } else if (activeTab === 'bs' && !bs) {
          const data = await apiRequest(`/api/finance/balance-sheet${orgParam}`);
          setBS(data);
        } else if (activeTab === 'cf' && !cf) {
          const data = await apiRequest(`/api/finance/cash-flow${orgParam}`);
          setCF(data);
        }
      } catch (e) {
        console.error('Finance fetch error:', e);
      } finally {
        setFinLoading(false);
      }
    };
    if (activeTab !== 'overview') run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentOrganizationId]);

  // Get location name by ID
  const getLocationName = (locationId: string) => {
    const location = locations.find(loc => loc.id === locationId);
    return location ? location.name : locationId;
  };

  // Get display title for current selection
  const getDisplayTitle = () => {
    if (selectedLocationIds.includes('all')) {
      return 'All Locations';
    } else if (selectedLocationIds.length === 1) {
      return getLocationName(selectedLocationIds[0]);
    } else {
      return `${selectedLocationIds.length} Locations Combined`;
    }
  };

  // Load metrics using React Query
  const locationIdSet = useMemo(() => new Set(locations.map(l => l.id)), [locations]);
  const selectionValid = selectedLocationIds.every(id => id === 'all' || locationIdSet.has(id));

  const { data: metrics = [], isLoading: metricsLoading } = useQuery<Metric[]>({
    queryKey: ['dashboard-metrics', currentOrganizationId, selectedLocationIds, timeRange],
    queryFn: async () => {
      console.log(`📊 Loading metrics for org: ${currentOrganizationId}, locations:`, selectedLocationIds);
      
      if (!selectedLocationIds || selectedLocationIds.length === 0) return [];
      
      let apiCalls: Promise<Metric[]>[] = [];
      
      if (selectedLocationIds.includes('all')) {
        // Load all locations for current organization
        const orgParam = currentOrganizationId ? `?organizationId=${currentOrganizationId}` : '';
        apiCalls = [apiRequest<Metric[]>(`/api/metrics${orgParam}`)];
      } else {
        // Load specific locations for current organization
        apiCalls = selectedLocationIds.map(locationId => {
          const orgParam = currentOrganizationId ? `&organizationId=${currentOrganizationId}` : '';
          return apiRequest<Metric[]>(`/api/metrics?locationId=${locationId}${orgParam}`);
        });
      }
      
      const dataArrays = await Promise.all(apiCalls);
      const allMetrics = dataArrays.flat();
      console.log(`📊 Loaded ${allMetrics.length} total metrics for org ${currentOrganizationId}`);
      
      // Return metrics based on selection type
      if (selectedLocationIds.includes('all') || selectedLocationIds.length === 1) {
        // Show as-is
        return allMetrics;
      } else {
        // Multi-select - aggregate the data
        return aggregateMetrics(allMetrics, selectedLocationIds);
      }
    },
    enabled: !!currentOrganizationId && selectedLocationIds.length > 0 && selectionValid,
    staleTime: 0, // Force refetch on organization change
    refetchOnWindowFocus: false,
  });

  // Group metrics by location for "All Locations" view
  const groupedMetrics = selectedLocationIds.includes('all') 
    ? metrics.reduce((groups, metric) => {
        const locationId = metric.locationId;
        if (!groups[locationId]) {
          groups[locationId] = [];
        }
        groups[locationId].push(metric);
        return groups;
      }, {} as Record<string, Metric[]>)
    : { [selectedLocationIds.length > 1 ? 'multi-select' : selectedLocationIds[0]]: metrics };

  // Locations are provided by LocationContext

  if (metricsLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Performance Dashboard</h1>
            <p className="text-muted-foreground">{getDisplayTitle()}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-gray-200 animate-pulse rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Location Selector */}
      <div className="flex flex-wrap items-center justify-between mb-6">
        <div className="flex items-center space-x-2 mb-4 md:mb-0">
          <h1 className="text-2xl font-medium">Performance Dashboard</h1>
          {selectedLocationIds.length > 1 && (
            <span className="text-sm bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200 px-2 py-1 rounded">
              {selectedLocationIds.length} locations selected
            </span>
          )}
          <span 
            className="mdi mdi-information-outline text-neutral-500 dark:text-neutral-400 cursor-help" 
            title="Overview of your location's key performance indicators"
          ></span>
        </div>

        <div className="flex flex-wrap gap-3 items-center">

          <div className="relative">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Select time range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="last7days">Last 7 Days</SelectItem>
                <SelectItem value="last30days">Last 30 Days</SelectItem>
                <SelectItem value="lastQuarter">Last Quarter</SelectItem>
                <SelectItem value="yearToDate">Year to Date</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button variant="outline" size="icon">
            <span className="mdi mdi-download text-neutral-700 dark:text-neutral-300"></span>
          </Button>
        </div>
      </div>

      {/* Financial View Tabs */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <Button variant={activeTab==='overview' ? 'outline' : 'ghost'} onClick={() => setActiveTab('overview')}>Overview</Button>
          <Button variant={activeTab==='pl' ? 'outline' : 'ghost'} onClick={async () => {
            const orgParam = currentOrganizationId ? `?organizationId=${currentOrganizationId}` : '';
            const data = await apiRequest(`/api/finance/profit-loss${orgParam}`);
            setPL(data);
            setActiveTab('pl');
          }}>Profit & Loss</Button>
          <Button variant={activeTab==='bs' ? 'outline' : 'ghost'} onClick={async () => {
            const orgParam = currentOrganizationId ? `?organizationId=${currentOrganizationId}` : '';
            const data = await apiRequest(`/api/finance/balance-sheet${orgParam}`);
            setBS(data);
            setActiveTab('bs');
          }}>Balance Sheet</Button>
          <Button variant={activeTab==='cf' ? 'outline' : 'ghost'} onClick={async () => {
            const orgParam = currentOrganizationId ? `?organizationId=${currentOrganizationId}` : '';
            const data = await apiRequest(`/api/finance/cash-flow${orgParam}`);
            setCF(data);
            setActiveTab('cf');
          }}>Cash Flow</Button>
        </div>
      </div>

      {/* Financial Statements Full View */}
      {activeTab !== 'overview' && (
        <div className="space-y-6 mb-6">
          {activeTab === 'pl' && (
            pl ? <FinancialStatementPL data={pl} organizationId={(currentOrganizationId ?? user.organizationId) || ''} /> : <EmptyFinance loading={finLoading} label="Profit & Loss" />
          )}
          {activeTab === 'bs' && (
            bs ? <FinancialStatementBS data={bs} organizationId={(currentOrganizationId ?? user.organizationId) || ''} /> : <EmptyFinance loading={finLoading} label="Balance Sheet" />
          )}
          {activeTab === 'cf' && (
            cf ? <FinancialStatementCF data={cf} organizationId={(currentOrganizationId ?? user.organizationId) || ''} /> : <EmptyFinance loading={finLoading} label="Cash Flow" />
          )}
        </div>
      )}

      {/* Metrics Grid (Overview only) */}
      {activeTab === 'overview' && (
      <div className="space-y-8">
        {Object.entries(groupedMetrics).map(([locationId, locationMetrics]) => (
          <div key={locationId} className="space-y-4">
            {/* Location Header for "All Locations" view */}
            {selectedLocationIds.includes('all') && (
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold">{getLocationName(locationId)}</h2>
                <span className="text-sm bg-gray-100 text-gray-700 px-2 py-1 rounded">
                  {locationMetrics.length} metrics
                </span>
              </div>
            )}
            
            {/* Multi-select header */}
            {locationId === 'multi-select' && (
              <div className="flex flex-wrap items-start gap-2">
                <h2 className="text-xl font-semibold">Combined Results</h2>
                <details className="text-sm bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200 px-2 py-1 rounded max-w-full">
                  <summary className="cursor-pointer">{selectedLocationIds.length} locations combined · Show list</summary>
                  <ul className="mt-2 space-y-1">
                    {selectedLocationIds.map(id => <li key={id}>{getLocationName(id)}</li>)}
                  </ul>
                </details>
              </div>
            )}
            
            {/* Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {locationMetrics.map((metric) => {
                const percentComplete = calculatePercentage(metric.value, metric.goal);
                const kpiData = {
                  id: metric.id,
                  title: metric.name,
                  value: typeof metric.value === 'number' ? metric.value.toLocaleString() : metric.value,
                  goal: typeof metric.goal === 'number' ? metric.goal.toLocaleString() : metric.goal,
                  percentComplete,
                  status: metric.status,
                  trend: 0, // Default trend
                  icon: getKpiIcon(metric.category),
                  category: metric.category,
                  subtitle: selectedLocationIds.includes('all') ? getLocationName(metric.locationId) : undefined
                };
                
                return (
                  <KPICard
                    key={`${metric.locationId}-${metric.id}`}
                    kpi={kpiData}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Main Content Area (Overview only) */}
      {activeTab === 'overview' && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <RevenueChart 
            locationId={selectedLocationId || 'all'} 
            organizationId={(currentOrganizationId ?? undefined) as string | undefined} 
          />
          <MeetingSummary 
            locationId={selectedLocationId || 'all'} 
            organizationId={(currentOrganizationId ?? undefined) as string | undefined} 
          />
        </div>

        <div className="space-y-6">
          <TeamScorecard 
            locationId={selectedLocationId || 'all'} 
            organizationId={(currentOrganizationId ?? undefined) as string | undefined} 
          />
          <CoachingSessions 
            userId={user.id} 
            organizationId={(currentOrganizationId ?? undefined) as string | undefined} 
          />
          <TemplateLibrary 
            organizationId={currentOrganizationId || user.organizationId} 
          />
        </div>
      </div>
      )}

      {/* Remove the AIAssistant component since it's already in MainLayout */}
      {/* <AIAssistant /> */}
    </div>
  );
}

function EmptyFinance({ loading, label }: { loading: boolean, label: string }) {
  return (
    <div className="rounded-lg border p-4 text-sm text-muted-foreground">
      {loading ? `Loading ${label}...` : `No ${label} data`}
    </div>
  );
}

function FinancialStatementPL({ data, organizationId }: { data: any, organizationId: string }) {
  const lines = [
    { name: 'Revenue', value: data.revenue },
    { name: 'COGS', value: -data.cogs },
    { name: 'Gross Profit', value: data.revenue - data.cogs, bold: true },
    { name: 'Operating Expenses', value: -data.opEx },
    { name: 'EBITDA', value: data.ebitda, bold: true },
  ];
  return (
    <div className="rounded-lg border p-4">
      <h3 className="font-medium mb-2">Profit & Loss</h3>
      <table className="w-full text-sm">
        <tbody>
          {lines.map((l) => (
            <tr key={l.name}>
              <td className={l.bold ? 'font-semibold py-1' : 'py-1'}>{l.name}</td>
              <td className={l.bold ? 'font-semibold text-right' : 'text-right'}>
                ${Math.round(l.value).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FinancialStatementBS({ data, organizationId }: { data: any, organizationId: string }) {
  const lines = [
    { name: 'Assets', value: data.assets },
    { name: 'Liabilities', value: -data.liabilities },
    { name: 'Equity', value: data.equity, bold: true },
  ];
  return (
    <div className="rounded-lg border p-4">
      <h3 className="font-medium mb-2">Balance Sheet</h3>
      <table className="w-full text-sm">
        <tbody>
          {lines.map((l) => (
            <tr key={l.name}>
              <td className={l.bold ? 'font-semibold py-1' : 'py-1'}>{l.name}</td>
              <td className={l.bold ? 'font-semibold text-right' : 'text-right'}>
                ${Math.round(l.value).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FinancialStatementCF({ data, organizationId }: { data: any, organizationId: string }) {
  const lines = [
    { name: 'Operating', value: data.operating },
    { name: 'Investing', value: data.investing },
    { name: 'Financing', value: data.financing },
    { name: 'Net Change in Cash', value: data.net, bold: true },
  ];
  return (
    <div className="rounded-lg border p-4">
      <h3 className="font-medium mb-2">Cash Flow</h3>
      <table className="w-full text-sm">
        <tbody>
          {lines.map((l) => (
            <tr key={l.name}>
              <td className={l.bold ? 'font-semibold py-1' : 'py-1'}>{l.name}</td>
              <td className={l.bold ? 'font-semibold text-right' : 'text-right'}>
                ${Math.round(l.value).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
